import { forwardRef, type CSSProperties } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  type QuickQuote,
  type QuoteLineItem,
  PAYMENT_CONDITION_LABELS,
} from "@/hooks/useQuickQuotes";
import { groupLineItems, filterClientVisibleItems } from "@/lib/quote-line-items";
import { formatPhone } from "@/lib/deal-helpers";
import {
  derivedQuoteFinancials,
  CONNECTION_LABELS_PT,
  type DerivedQuoteFinancials,
} from "@/lib/quote-financials";
import type { ConnectionType } from "@/lib/solar-calculator";
import {
  calculateFinancialAnalysis,
  generateInsights,
  PERIODS,
} from "@/lib/financial-analysis";
import { QuoteInvestmentScenarios } from "@/components/cotacoes/quote-shared/QuoteInvestmentScenarios";
import { QuoteInsightsBlock } from "@/components/cotacoes/quote-shared/QuoteInsightsBlock";
import { QuoteCashFlowStaticChart } from "@/components/cotacoes/quote-shared/QuoteCashFlowStaticChart";

// ─────────────── Types ───────────────

export interface QuickQuotePdfData {
  quote: QuickQuote;
  tenant: {
    name: string;
    logo_url: string | null;
    /** Variante clara da logo, para fundos coloridos (header). Opcional. */
    logo_url_white: string | null;
    brand_primary_color: string | null; // hex (#RRGGBB)
    /** Tarifa em R$/kWh do tenant (solar_calc_config.default_tariff). Pode ser null. */
    default_tariff: number | null;
  };
  seller: {
    name: string | null;
    phone: string | null;
    email: string | null;
  };
  publicUrl: string | null; // ex: https://soluxcrm.lovable.app/p/cotacao/<slug>
}

interface QuickQuotePdfTemplateProps {
  data: QuickQuotePdfData;
}

// ─────────────── Helpers ───────────────

const DEFAULT_PRIMARY = "#10B981";

function brl(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(value);
}

function brlExact(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
}

function getPrimary(c: string | null | undefined): string {
  if (!c) return DEFAULT_PRIMARY;
  if (c.startsWith("#") && (c.length === 7 || c.length === 4)) return c;
  return DEFAULT_PRIMARY;
}

// Página A4
const A4: CSSProperties = {
  width: "794px",
  minHeight: "1123px",
  backgroundColor: "#ffffff",
  fontFamily: "Arial, sans-serif",
  color: "#0f172a",
  padding: "40px 48px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

// ─────────────── Component ───────────────

export const QuickQuotePdfTemplate = forwardRef<
  HTMLDivElement,
  QuickQuotePdfTemplateProps
>(({ data }, ref) => {
  const { quote, tenant, seller, publicUrl } = data;
  const primary = getPrimary(tenant.brand_primary_color);

  const showPage2 =
    quote.proposal_style === "complete" &&
    quote.include_financial_analysis === true;

  const investmentLabel =
    quote.total_value != null
      ? brlExact(quote.total_value)
      : `${brl(quote.investment_min)} – ${brl(quote.investment_max)}`;

  // Fonte única dos cálculos derivados (conta projetada, conta mínima,
  // compensação%, tarifa derivada). Não duplicar fórmulas no JSX.
  // Fonte única dos cálculos derivados. Trata consumption=0 como ausente
  // (F.1) para impedir cobertura "0%" em cotações modo "Por valor".
  const consumptionForFin =
    quote.consumption_kwh != null && quote.consumption_kwh > 0
      ? quote.consumption_kwh
      : null;
  const fin = derivedQuoteFinancials({
    bill_value: quote.bill_value,
    monthly_savings: quote.monthly_savings,
    consumption_kwh: consumptionForFin,
    estimated_generation_kwh: quote.estimated_generation_kwh,
    connection_type: quote.connection_type,
    cip_value: quote.cip_value,
    tariff: tenant.default_tariff,
  });

  const showFinancialBlocks =
    fin.currentBill > 0 && fin.monthlySavings > 0;

  // Análise financeira para os blocos extras da Page 2 (cenários e insights).
  // Espelha o cálculo feito em QuotationPublicPage.tsx para garantir paridade
  // de números entre o portal público e o PDF.
  const showPage2Extras =
    showPage2 && (quote.monthly_savings || 0) > 0;
  let financialAnalysis: ReturnType<typeof calculateFinancialAnalysis> | null = null;
  let insights: string[] = [];
  if (showPage2Extras) {
    try {
      financialAnalysis = calculateFinancialAnalysis({
        monthlySavings: quote.monthly_savings || 0,
        annualSavings: (quote.monthly_savings || 0) * 12,
        paybackYears: quote.payback_years || 0,
        economy25Years: quote.economy_25_years || 0,
        projectValue: quote.total_value || 0,
        currentBill: quote.bill_value || 0,
        estimatedGeneration: quote.estimated_generation_kwh || 0,
        consumption: consumptionForFin || 0,
        minimumBill: fin.minimumBill,
      });
      insights = generateInsights(financialAnalysis);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[QuickQuotePdfTemplate] financial analysis error:",
        (err as any)?.message || String(err),
      );
    }
  }

  return (
    <div ref={ref} aria-hidden="true">
      {/* PAGE 1 */}
      <Page primary={primary}>
        <Header
          primary={primary}
          tenant={tenant}
          quote={quote}
        />

        <ClientBlock quote={quote} />

        <KpiGrid
          primary={primary}
          investmentLabel={investmentLabel}
          quote={quote}
        />

        <TechnicalDetails quote={quote} primary={primary} fin={fin} />

        {showFinancialBlocks && (
          <NewBillBlock primary={primary} fin={fin} />
        )}

        {showFinancialBlocks && (
          <HowWeGotHereBlock
            primary={primary}
            fin={fin}
            totalValue={quote.total_value || 0}
            connectionType={quote.connection_type}
            generation={quote.estimated_generation_kwh}
            consumption={quote.consumption_kwh}
          />
        )}

        {quote.line_items &&
          filterClientVisibleItems(quote.line_items as QuoteLineItem[]).length > 0 && (
            <ItemsBlock quote={quote} primary={primary} />
          )}

        {(quote.payment_observations ||
          quote.payment_condition ||
          (quote.structured_installments &&
            (quote.structured_installments as any[]).length > 0) ||
          quote.warranty_text ||
          quote.observations) && (
          <TermsBlock quote={quote} primary={primary} />
        )}

        <div style={{ flex: 1 }} />

        <Footer
          primary={primary}
          tenant={tenant}
          seller={seller}
          quote={quote}
          publicUrl={publicUrl}
        />
      </Page>

      {/* PAGE 2 (condicional) — Comparativo 25 anos + Solar vs CDI/Poupança */}
      {showPage2 && (
        <Page primary={primary}>
          <SectionHeader title="Análise Financeira" primary={primary} />

          <FinancialComparison quote={quote} />

          {showPage2Extras &&
            (quote.total_value || 0) > 0 &&
            (quote.monthly_savings || 0) > 0 && (
              <div style={{ marginTop: "16px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#0f172a",
                    marginBottom: "8px",
                  }}
                >
                  Fluxo de caixa acumulado
                </div>
                <QuoteCashFlowStaticChart
                  totalValue={quote.total_value || 0}
                  monthlySavings={quote.monthly_savings || 0}
                  primaryColor={primary}
                  width={698}
                  height={260}
                />
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    marginTop: "6px",
                  }}
                >
                  Vermelho: investimento ainda não recuperado. Verde: lucro
                  acumulado.
                </p>
              </div>
            )}

          {showPage2Extras && financialAnalysis && (
            <div
              style={{
                marginTop: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <QuoteInvestmentScenarios
                scenarios={financialAnalysis.scenarios}
                periods={PERIODS}
              />

              {insights.length > 0 && (
                <QuoteInsightsBlock insights={insights} limit={4} />
              )}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <Footer
            primary={primary}
            tenant={tenant}
            seller={seller}
            quote={quote}
            publicUrl={publicUrl}
          />
        </Page>
      )}
    </div>
  );
});

QuickQuotePdfTemplate.displayName = "QuickQuotePdfTemplate";

// ─────────────── Sub-components ───────────────

function Page({
  primary: _primary,
  children,
}: {
  primary: string;
  children: React.ReactNode;
}) {
  return <div style={A4}>{children}</div>;
}

function Header({
  primary,
  tenant,
  quote,
}: {
  primary: string;
  tenant: QuickQuotePdfData["tenant"];
  quote: QuickQuote;
}) {
  const initial = (tenant.name || "?").trim().charAt(0).toUpperCase();
  // Logo branca tem prioridade no header colorido (sem fundo branco).
  // Se só houver a logo normal, mantém o fundo branco para garantir contraste.
  const useWhiteLogo = !!tenant.logo_url_white;
  const logoSrc = tenant.logo_url_white || tenant.logo_url;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: primary,
        color: "#ffffff",
        padding: "12px 18px",
        borderRadius: "6px",
        minHeight: "72px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        {logoSrc ? (
          <img
            src={logoSrc}
            crossOrigin="anonymous"
            alt={tenant.name}
            style={{
              // Logo proporcional: cresce até 56px (≈78% do header de 72px),
              // nunca menor que 32px para legibilidade. Largura livre, máx 180px
              // para não invadir o lado do código da cotação.
              maxHeight: "56px",
              minHeight: "32px",
              maxWidth: "180px",
              width: "auto",
              height: "auto",
              borderRadius: "6px",
              objectFit: "contain",
              backgroundColor: useWhiteLogo ? "transparent" : "#ffffff",
              padding: useWhiteLogo ? "0" : "4px",
            }}
          />
        ) : (
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "6px",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            {initial}
          </div>
        )}
        <div>
          <div style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1.1 }}>
            {tenant.name}
          </div>
          <div style={{ fontSize: "11px", opacity: 0.92 }}>Cotação Solar</div>
        </div>
      </div>
      <div style={{ textAlign: "right", fontSize: "11px", opacity: 0.95 }}>
        <div style={{ fontFamily: "monospace", fontWeight: 600 }}>
          {quote.code}
        </div>
        <div>{fmtDate(quote.created_at)}</div>
      </div>
    </div>
  );
}

function ClientBlock({ quote }: { quote: QuickQuote }) {
  const tipo =
    quote.client_type === "residential"
      ? "Residencial"
      : quote.client_type === "commercial"
        ? "Comercial"
        : "Rural";
  const cityState = [quote.client_city, quote.client_state]
    .filter(Boolean)
    .join(" / ");

  return (
    <div style={{ padding: "16px 4px", borderBottom: "1px solid #e5e7eb" }}>
      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>
        Cotação preparada para
      </div>
      <div style={{ fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
        {quote.client_name || "Cliente"}
      </div>
      <div style={{ fontSize: "12px", color: "#475569", marginTop: "2px" }}>
        {[cityState, tipo].filter(Boolean).join(" • ")}
      </div>
    </div>
  );
}

function KpiCard({
  primary,
  label,
  value,
}: {
  primary: string;
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        borderTop: `3px solid ${primary}`,
        backgroundColor: "#f8fafc",
        borderRadius: "6px",
        padding: "7px 12px",
      }}
    >
      <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
        {value}
      </div>
    </div>
  );
}

function KpiGrid({
  primary,
  investmentLabel,
  quote,
}: {
  primary: string;
  investmentLabel: string;
  quote: QuickQuote;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px",
        marginTop: "16px",
      }}
    >
      <KpiCard primary={primary} label="Investimento" value={investmentLabel} />
      <KpiCard
        primary={primary}
        label="Economia mensal"
        value={brlExact(quote.monthly_savings)}
      />
      <KpiCard
        primary={primary}
        label="Payback"
        value={
          quote.payback_years != null
            ? `${quote.payback_years.toFixed(1)} anos`
            : "—"
        }
      />
      <KpiCard
        primary={primary}
        label="Potência"
        value={
          quote.power_kwp != null ? `${quote.power_kwp.toFixed(2)} kWp` : "—"
        }
      />
    </div>
  );
}

function TechnicalDetails({
  quote,
  primary,
  fin,
}: {
  quote: QuickQuote;
  primary: string;
  fin: DerivedQuoteFinancials;
}) {
  const rows: Array<[string, string]> = [];
  if (quote.power_kwp != null)
    rows.push(["Potência total", `${quote.power_kwp.toFixed(2)} kWp`]);
  if (quote.panel_count != null) {
    const wattsSuffix =
      quote.panel_watts_used != null && quote.panel_watts_used > 0
        ? ` × ${quote.panel_watts_used}W`
        : "";
    rows.push(["Nº de painéis", `${quote.panel_count} un${wattsSuffix}`]);
  }
  if (quote.estimated_generation_kwh != null)
    rows.push([
      "Geração estimada/mês",
      `${Math.round(quote.estimated_generation_kwh)} kWh`,
    ]);
  // Consumo médio mensal — só aparece se persistido em consumption_kwh.
  // Evita renderizar "0 kWh" ou "—" quando o vendedor não informou.
  if (quote.consumption_kwh != null && quote.consumption_kwh > 0) {
    rows.push([
      "Consumo médio mensal",
      `${Math.round(quote.consumption_kwh)} kWh`,
    ]);
    // Compensação — usa fonte única (derivedQuoteFinancials).
    if (fin.compensationPct != null) {
      rows.push([
        "Compensação estimada",
        `${Math.round(fin.compensationPct)}%`,
      ]);
    }
  }
  // Linhas financeiras extras — paridade com página pública (Onda 1-quater.5).
  if (quote.monthly_savings != null && quote.monthly_savings > 0) {
    rows.push(["Economia mensal", brlExact(quote.monthly_savings)]);
  }
  if (quote.economy_25_years != null && quote.economy_25_years > 0) {
    rows.push(["Economia em 25 anos", brlExact(quote.economy_25_years)]);
  }

  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: "16px" }}>
      <SectionHeader title="Detalhes Técnicos" primary={primary} />
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {rows.map(([label, value], i) => (
          <div
            key={label}
            style={{
              display: "flex",
              padding: "5px 14px",
              fontSize: "12px",
              backgroundColor: i % 2 === 0 ? "#ffffff" : "#f8fafc",
            }}
          >
            <div style={{ flex: 1, color: "#64748b" }}>{label}</div>
            <div style={{ fontWeight: 600, color: "#0f172a" }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermsBlock({
  quote,
  primary,
}: {
  quote: QuickQuote;
  primary: string;
}) {
  const installments = (quote.structured_installments || []) as Array<{
    label?: string;
    percent?: number;
    daysOffset?: number;
  }>;

  const rowStyle: CSSProperties = {
    fontSize: "11px",
    color: "#0f172a",
    marginBottom: "4px",
    lineHeight: 1.5,
  };
  const labelStyle: CSSProperties = {
    fontWeight: 700,
    color: "#475569",
    marginRight: "4px",
  };

  // Monta texto de condição de pagamento (label + parcelas inline)
  const paymentText = quote.payment_condition
    ? PAYMENT_CONDITION_LABELS[quote.payment_condition]
    : null;
  const installmentsText =
    installments.length > 0
      ? installments
          .map(
            (p, i) =>
              `${p.label || `Parcela ${i + 1}`}${p.percent != null ? ` (${p.percent}%)` : ""}`,
          )
          .join(" • ")
      : null;

  return (
    <div style={{ marginTop: "16px" }}>
      <SectionHeader title="Termos da cotação" primary={primary} />
      {(paymentText || installmentsText || quote.payment_observations) && (
        <div style={rowStyle}>
          <span style={labelStyle}>Condição de pagamento:</span>
          {paymentText && <span>{paymentText}</span>}
          {installmentsText && (
            <span style={{ color: "#475569" }}> — {installmentsText}</span>
          )}
          {quote.payment_observations && (
            <span style={{ color: "#475569" }}>
              {paymentText || installmentsText ? " · " : ""}
              {quote.payment_observations}
            </span>
          )}
        </div>
      )}
      {quote.warranty_text && (
        <div style={rowStyle}>
          <span style={labelStyle}>Garantia:</span>
          <span style={{ whiteSpace: "pre-wrap" }}>{quote.warranty_text}</span>
        </div>
      )}
      {quote.observations && (
        <div style={rowStyle}>
          <span style={labelStyle}>Observações:</span>
          <span style={{ whiteSpace: "pre-wrap" }}>{quote.observations}</span>
        </div>
      )}
    </div>
  );
}

function ItemsBlock({
  quote,
  primary,
}: {
  quote: QuickQuote;
  primary: string;
}) {
  const allItems = filterClientVisibleItems(
    (quote.line_items || []) as QuoteLineItem[],
  );
  // Agrupa componentes de kit sob o pai. Sub-itens são exibidos indentados
  // (├ / └), sem preço.
  const groups = groupLineItems(allItems);
  const visibleParents = groups.map((g) => g.item);
  const hasPrice = visibleParents.some(
    (i) => Number(i.unit_price || 0) > 0,
  );
  const subtotal = visibleParents.reduce(
    (acc, i) => acc + Number(i.total || 0),
    0,
  );
  const discountAmount =
    quote.discount_value > 0
      ? quote.discount_type === "percent"
        ? (subtotal * quote.discount_value) / 100
        : quote.discount_value
      : 0;

  return (
    <div style={{ marginTop: "16px" }}>
      <SectionHeader title="Equipamentos" primary={primary} />
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          overflow: "hidden",
          fontSize: "12px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: hasPrice ? "1fr 60px 100px 100px" : "1fr 60px",
            backgroundColor: "#f1f5f9",
            padding: "5px 12px",
            fontWeight: 600,
            color: "#475569",
            fontSize: "11px",
          }}
        >
          <div>Descrição</div>
          <div style={{ textAlign: "right" }}>Qtd</div>
          {hasPrice && (
            <>
              <div style={{ textAlign: "right" }}>Preço/un</div>
              <div style={{ textAlign: "right" }}>Total</div>
            </>
          )}
        </div>
        {groups.map((g, idx) => (
          <div key={g.item.id || idx}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: hasPrice
                  ? "1fr 60px 100px 100px"
                  : "1fr 60px",
                padding: "5px 12px",
                backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                color: "#0f172a",
              }}
            >
              <div style={{ fontWeight: g.children.length > 0 ? 600 : 400 }}>
                {g.item.description}
              </div>
              <div style={{ textAlign: "right" }}>{g.item.quantity}</div>
              {hasPrice && (
                <>
                  <div style={{ textAlign: "right" }}>
                    {brlExact(g.item.unit_price)}
                  </div>
                  <div style={{ textAlign: "right", fontWeight: 600 }}>
                    {brlExact(g.item.total)}
                  </div>
                </>
              )}
            </div>
            {/* Sub-itens do kit — indentados, sem preço */}
            {g.children.map((child, ci) => {
              const isLast = ci === g.children.length - 1;
              return (
                <div
                  key={child.id || `c-${ci}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: hasPrice
                      ? "1fr 60px 100px 100px"
                      : "1fr 60px",
                    padding: "4px 12px 4px 28px",
                    backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  <div>
                    <span style={{ fontFamily: "monospace", marginRight: "6px" }}>
                      {isLast ? "└" : "├"}
                    </span>
                    {child.quantity}× {child.description}
                  </div>
                  <div />
                  {hasPrice && (
                    <>
                      <div />
                      <div />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {/* Totais — fora da tabela, alinhados à direita com respiro */}
      {hasPrice && (
        <div
          style={{
            marginTop: "8px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            style={{
              minWidth: "260px",
              fontSize: "11px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {quote.discount_value > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "#64748b",
                  }}
                >
                  <span>Subtotal:</span>
                  <span>{brlExact(subtotal)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "#dc2626",
                  }}
                >
                  <span>Desconto:</span>
                  <span>-{brlExact(discountAmount)}</span>
                </div>
                <div
                  style={{
                    borderTop: "1px solid #e5e7eb",
                    marginTop: "2px",
                  }}
                />
              </>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                color: primary,
                fontSize: "13px",
                paddingTop: "2px",
              }}
            >
              <span>Total:</span>
              <span>{brlExact(quote.total_value || 0)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Footer({
  primary,
  tenant,
  seller,
  quote,
  publicUrl,
}: {
  primary: string;
  tenant: QuickQuotePdfData["tenant"];
  seller: QuickQuotePdfData["seller"];
  quote: QuickQuote;
  publicUrl: string | null;
}) {
  return (
    <div
      style={{
        marginTop: "20px",
        paddingTop: "12px",
        borderTop: `2px solid ${primary}`,
        fontSize: "10px",
        color: "#475569",
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: "#0f172a" }}>{tenant.name}</div>
        {seller.name && (
          <div style={{ marginTop: "2px" }}>
            Vendedor: {seller.name}
            {seller.phone ? ` • ${formatPhone(seller.phone) ?? seller.phone}` : ""}
          </div>
        )}
        <div style={{ marginTop: "4px" }}>
          {quote.expires_at
            ? `Válida até: ${fmtDate(quote.expires_at)}`
            : "Sem validade definida"}
        </div>
        <div style={{ marginTop: "4px", color: "#64748b", fontStyle: "italic" }}>
          Valores são estimativas sujeitas a vistoria técnica.
        </div>
        {publicUrl && (
          <div style={{ marginTop: "6px", color: primary, wordBreak: "break-all" }}>
            {publicUrl}
          </div>
        )}
      </div>
      {publicUrl && (
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "4px",
            borderRadius: "4px",
            border: "1px solid #e5e7eb",
          }}
        >
          <QRCodeSVG value={publicUrl} size={56} level="M" />
        </div>
      )}
    </div>
  );
}

function SectionHeader({
  title,
  primary,
  style,
}: {
  title: string;
  primary: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: primary,
        marginBottom: "8px",
        ...style,
      }}
    >
      {title}
    </div>
  );
}

function FinancialComparison({ quote }: { quote: QuickQuote }) {
  const billValue = quote.bill_value || 0;
  const monthlySavings = quote.monthly_savings || 0;
  const totalValue = quote.total_value || 0;

  // Cálculos puros (sem hooks) — padrão Core: 6% inflação energia, 0,5% degradação
  let totalEnergyCost = 0;
  let totalSavings = 0;
  for (let n = 0; n < 25; n++) {
    totalEnergyCost += billValue * 12 * Math.pow(1.06, n);
    totalSavings +=
      monthlySavings * 12 * Math.pow(1.06, n) * Math.pow(0.995, n);
  }
  const cdi = totalValue * Math.pow(1.1365, 25) * 0.85;
  const poupanca = totalValue * Math.pow(1.083, 25);
  const solarVsCdi = cdi > 0 ? totalSavings / cdi : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Linha 1: Sem solar vs Com solar */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#991b1b",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Sem solar (25 anos)
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#dc2626",
              marginTop: "6px",
            }}
          >
            {totalEnergyCost > 0 ? brlExact(totalEnergyCost) : "—"}
          </div>
          <div style={{ fontSize: "11px", color: "#991b1b", marginTop: "4px" }}>
            Total pago à concessionária com inflação 6% a.a.
          </div>
        </div>
        <div
          style={{
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid #bbf7d0",
            backgroundColor: "#f0fdf4",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#166534",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Com solar (25 anos)
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#16a34a",
              marginTop: "6px",
            }}
          >
            {totalSavings > 0 ? `+ ${brlExact(totalSavings)}` : "—"}
          </div>
          <div style={{ fontSize: "11px", color: "#166534", marginTop: "4px" }}>
            Economia acumulada já considerando degradação dos painéis
          </div>
          {totalValue > 0 && totalSavings > 0 && (
            <div
              style={{
                fontSize: "11px",
                color: "#166534",
                marginTop: "4px",
                fontWeight: 600,
              }}
            >
              Ganho líquido em 25 anos (descontando investimento de{" "}
              {brlExact(totalValue)}): {brlExact(totalSavings - totalValue)}
            </div>
          )}
        </div>
      </div>

      {/* Comparativo de investimento — só se houver totalValue */}
      {totalValue > 0 && (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#0f172a",
              marginBottom: "4px",
            }}
          >
            Solar vs aplicações financeiras em 25 anos
          </div>
          <div
            style={{
              fontSize: "10px",
              color: "#64748b",
              marginBottom: "10px",
              lineHeight: 1.4,
            }}
          >
            Solar: economia mensal acumulada em 25 anos (inflação 6% a.a.,
            degradação 0,5% a.a. dos painéis). CDI/Poupança: rendimento de
            aplicação única de {brlExact(totalValue)} pelo mesmo período.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px",
            }}
          >
            <div
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                backgroundColor: "#ecfdf5",
                border: "1px solid #a7f3d0",
              }}
            >
              <div style={{ fontSize: "10px", color: "#065f46" }}>
                ☀ Solar (isento IR)
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#047857",
                  marginTop: "4px",
                }}
              >
                {brlExact(totalSavings)}
              </div>
            </div>
            <div
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ fontSize: "10px", color: "#475569" }}>
                CDI líq. (15% IR)
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginTop: "4px",
                }}
              >
                {brlExact(cdi)}
              </div>
            </div>
            <div
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ fontSize: "10px", color: "#475569" }}>
                Poupança
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginTop: "4px",
                }}
              >
                {brlExact(poupanca)}
              </div>
            </div>
          </div>
          {solarVsCdi > 1 && (
            <div
              style={{
                fontSize: "11px",
                color: "#047857",
                marginTop: "8px",
                fontWeight: 600,
              }}
            >
              Solar é {solarVsCdi.toFixed(1)}× mais vantajoso que o CDI
              líquido no longo prazo.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────── "Sua nova conta de luz" ───────────────
// Cards comparativos: conta atual vs conta projetada (após o solar).
// Conta projetada = max(minimumBill, currentBill - monthlySavings) — vem de
// derivedQuoteFinancials. Não duplicar fórmula aqui.

function NewBillBlock({
  primary,
  fin,
}: {
  primary: string;
  fin: DerivedQuoteFinancials;
}) {
  return (
    <div style={{ marginTop: "16px", pageBreakInside: "avoid" }}>
      <SectionHeader title="Sua nova conta de luz" primary={primary} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
        }}
      >
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: "#991b1b",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Conta atual
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#991b1b",
              marginTop: "2px",
            }}
          >
            {brlExact(fin.currentBill)}
          </div>
        </div>
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            backgroundColor: "#f0fdf4",
            border: `1px solid ${primary}`,
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: primary,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Conta projetada
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: primary,
              marginTop: "2px",
            }}
          >
            {brlExact(fin.projectedBill)}
          </div>
          <div
            style={{
              fontSize: "10px",
              color: "#475569",
              marginTop: "1px",
            }}
          >
            inclui taxa mínima da concessionária
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: "6px",
          textAlign: "center",
          fontSize: "12px",
          fontWeight: 700,
          color: primary,
        }}
      >
        ↓ Economia mensal: {brlExact(fin.monthlySavings)} ↓
      </div>
    </div>
  );
}

// ─────────────── "Como chegamos nesses números" ───────────────
// Memorial de cálculo simplificado, espelhando o bloco da Proposta Comercial
// (src/components/crm/opportunity/ProposalPdfTemplate.tsx ~569-636).
// Adaptado para a brevidade da Cotação Rápida.

function HowWeGotHereBlock({
  primary,
  fin,
  totalValue,
  connectionType,
  generation,
  consumption,
}: {
  primary: string;
  fin: DerivedQuoteFinancials;
  totalValue: number;
  connectionType: string | null;
  generation: number | null;
  consumption: number | null;
}) {
  const paybackYears =
    fin.annualSavings > 0 ? totalValue / fin.annualSavings : 0;
  const connLabel =
    CONNECTION_LABELS_PT[(connectionType as ConnectionType) || "bifasico"] ||
    "bifásica";
  const showCoverage =
    consumption != null &&
    consumption > 0 &&
    generation != null &&
    generation > consumption;

  const numberStyle: CSSProperties = {
    fontSize: "11px",
    fontWeight: 700,
    color: primary,
    marginBottom: "2px",
  };
  const textStyle: CSSProperties = {
    fontSize: "11px",
    color: "#0f172a",
    lineHeight: 1.5,
    marginBottom: "8px",
  };

  return (
    <div style={{ marginTop: "16px", pageBreakInside: "avoid" }}>
      <SectionHeader title="Como chegamos nesses números" primary={primary} />

      <div style={numberStyle}>1. Economia mensal</div>
      <div style={textStyle}>
        Sua conta hoje é <strong>{brlExact(fin.currentBill)}</strong>. Com a
        geração estimada do sistema, sua nova conta cai para{" "}
        <strong>{brlExact(fin.projectedBill)}</strong> — uma economia de{" "}
        <strong>{brlExact(fin.monthlySavings)}</strong> por mês (
        {brlExact(fin.annualSavings)} por ano).
      </div>

      {totalValue > 0 && fin.annualSavings > 0 && (
        <>
          <div style={numberStyle}>2. Tempo de retorno (payback)</div>
          <div style={textStyle}>
            Com a economia mensal acima, o investimento de{" "}
            <strong>{brlExact(totalValue)}</strong> se paga em aproximadamente{" "}
            <strong>{paybackYears.toFixed(1)} anos</strong>. Depois disso, a
            economia continua por mais de duas décadas.
          </div>
        </>
      )}

      <div style={numberStyle}>3. Conta mínima da concessionária</div>
      <div style={textStyle}>
        Mesmo gerando a própria energia, a concessionária cobra uma taxa
        mínima pela disponibilidade da rede. Para uma conexão {connLabel},
        esse piso é de cerca de <strong>{brlExact(fin.minimumBill)}</strong>{" "}
        por mês ({fin.connectionMinKwh} kWh ×{" "}
        {brlExact(fin.tariffUsed)} +{" "}
        {brlExact(fin.minimumBill - fin.connectionMinKwh * fin.tariffUsed)}{" "}
        de CIP).
      </div>

      {showCoverage && (
        <>
          <div style={numberStyle}>4. Cobertura do consumo</div>
          <div style={textStyle}>
            O sistema gera{" "}
            {Math.round(generation!).toLocaleString("pt-BR")} kWh/mês —
            compatível com o consumo de{" "}
            {Math.round(consumption!).toLocaleString("pt-BR")} kWh/mês. A
            margem adicional compensa perdas elétricas e degradação natural
            dos módulos.
          </div>
        </>
      )}
    </div>
  );
}
